import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * CRM Module Service Stores
 * Updated to match backend api-types
 */
export const crmStores: ServiceStoreConfig[] = [
  {
    name: "customers",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.customersStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalCustomers = entities.length;
          const activeCustomers = entities.filter(
            (customer: Record<string, unknown>) => customer.activeStatus === true
          ).length;
          const generalContractors = entities.filter(
            (customer: Record<string, unknown>) => customer.customerType === "general_contractor"
          ).length;
          const developers = entities.filter(
            (customer: Record<string, unknown>) => customer.customerType === "developer"
          ).length;

          return [
            {
              title: "Total Customers",
              value: () => totalCustomers.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeCustomers.toString(),
              valueColor: "success.main",
            },
            {
              title: "General Contractors",
              value: () => generalContractors.toString(),
              valueColor: "info.main",
            },
            {
              title: "Developers",
              value: () => developers.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "customer-satisfaction",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.customerSatisfactionStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalSurveys = entities.length;
          const averageRating = entities.length > 0
            ? entities.reduce((sum: number, survey: Record<string, unknown>) => sum + ((survey.rating as number) || 0), 0) / entities.length
            : 0;
          const highRatings = entities.filter(
            (survey: Record<string, unknown>) => (survey.rating as number) >= 4
          ).length;
          const lowRatings = entities.filter(
            (survey: Record<string, unknown>) => (survey.rating as number) <= 2
          ).length;

          return [
            {
              title: "Total Surveys",
              value: () => totalSurveys.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Average Rating",
              value: () => averageRating.toFixed(1),
              valueColor: "info.main",
            },
            {
              title: "High Ratings (4-5)",
              value: () => highRatings.toString(),
              valueColor: "success.main",
            },
            {
              title: "Low Ratings (1-2)",
              value: () => lowRatings.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "vendors",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.vendorsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalVendors = entities.length;
          const activeVendors = entities.filter(
            (vendor: Record<string, unknown>) => vendor.activeStatus === true
          ).length;
          const preferredVendors = entities.filter(
            (vendor: Record<string, unknown>) => vendor.preferredVendor === true
          ).length;
          const certifiedVendors = entities.filter(
            (vendor: Record<string, unknown>) => vendor.certifications && (vendor.certifications as unknown[]).length > 0
          ).length;

          return [
            {
              title: "Total Vendors",
              value: () => totalVendors.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeVendors.toString(),
              valueColor: "success.main",
            },
            {
              title: "Preferred",
              value: () => preferredVendors.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Certified",
              value: () => certifiedVendors.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "vendor-performance",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.vendorPerformanceStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalReviews = entities.length;
          const averageScore = entities.length > 0
            ? entities.reduce((sum: number, review: Record<string, unknown>) => sum + ((review.performanceScore as number) || 0), 0) / entities.length
            : 0;
          const topPerformers = entities.filter(
            (review: Record<string, unknown>) => (review.performanceScore as number) >= 4.5
          ).length;
          const underperformers = entities.filter(
            (review: Record<string, unknown>) => (review.performanceScore as number) < 3.0
          ).length;

          return [
            {
              title: "Total Reviews",
              value: () => totalReviews.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Average Score",
              value: () => averageScore.toFixed(2),
              valueColor: "info.main",
            },
            {
              title: "Top Performers (4.5+)",
              value: () => topPerformers.toString(),
              valueColor: "success.main",
            },
            {
              title: "Under 3.0",
              value: () => underperformers.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
